/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useRef, useState } from 'react';
import MultiSelectPopup from './MultiSelectPopup';
import ReactMarkdown from 'react-markdown';
import Attributes from './model/Attributes';
import Armor from './model/Armor';

interface ArmorSelectorProps {
  armorClass: number;
  armorDesc: string;
  armorType: Armor | null;
  dexterity: number;
  strength: number;
  isOpen: boolean;
  useMm2024: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSave: (ac: number, desc: string, armor: Armor | null) => void;
}

const ArmorSelector: React.FC<ArmorSelectorProps> = ({ armorClass, armorDesc, armorType, dexterity, strength, isOpen, useMm2024, setIsOpen, onSave }) => {
  const [hasShield, setHasShield] = useState<boolean>(false);
  const [isCustom, setIsCustom] = useState<boolean>(false);
  const [dexBonus, setDexBonus] = useState<number>(0);
  const [maxDexBonus, setMaxDexBonus] = useState<number>(0);
  const [deviation, setDeviation] = useState<number>(0);

  useEffect(() => {
    console.log(`ArmorSelect.useEffect: armorType: ${JSON.stringify(armorType)},`+
                ` armorDesc: ${armorDesc}, dexterity: ${dexterity}, hasShield: ${hasShield}`);
    const newMonster = !armorType;
    let newArmor: Armor | null = armorType || Armor.getArmor(armorDesc) || null;

    //////////////////////////
    let baseArmorDesc = armorDesc;
    let shield = false;
    if (armorDesc?.toLowerCase() === 'shield') {
      shield = true;
      baseArmorDesc = '';
    } else if (armorDesc?.toLowerCase().includes(', shield')) {
      shield = true;
      baseArmorDesc = armorDesc.replace(/, shield/gi, '');
    }
    if (hasShield !== shield) {
      setHasShield(shield);
    }
    if (!newArmor && armorDesc) {
      newArmor = customArmor(armorDesc, "heavy", armorClass);
    }

    let newAC = Armor.buildAC(newArmor, dexterity, shield);
    const newDeviation = armorClass - newAC;
    setDeviation(newDeviation);
    if (newArmor?.name?.startsWith('_') && newArmor.name.endsWith('_') && newDeviation == 0) {
      const newArmorName = newArmor.name.substring(1, newArmor.name.length-1);
      newArmor = Armor.getArmor(newArmorName) || newArmor;
    }

    const newDesc = buildDescription(newArmor, shield);

    if (!Armor.equals(armorType, newArmor) || armorDesc !== newDesc) {
      // we can't call onSave inside a useEffect, so do this asynchronously
      setTimeout(() => {
        if (newMonster) {
          // New monsters are allowed to set the armor type, but not the AC or description.
          onSave(armorClass, armorDesc, newArmor);
        } else {
          // Existing monsters are allow to set the description, but not the AC or armor type.
          onSave(armorClass, newDesc, armorType);
        }
      }, 10);
    }
  }, [armorType, armorDesc, dexterity, hasShield]);

  useEffect(() =>{
    setDexBonus(Attributes.getBonus(dexterity));
    setMaxDexBonus(Armor.buildDexBonus(armorType?.type || null, dexterity));
  }, [dexterity, armorType?.type]);

  const customArmor = (armorName: string, type: 'light' | 'medium' | 'heavy' | 'special' | 'minAc', ac: number): Armor => {
    if (armorName?.startsWith('_') && armorName.endsWith('_')) {
      armorName = armorName.substring(1, armorName.length-1);
    }
    const namedArmor = Armor.getArmor(armorName);
    if (namedArmor && namedArmor.ac === ac && namedArmor.type === type) {
      setIsCustom(false);
      return namedArmor;
    }

    // If we found an armor of the same name, but it provides a different level of AC or type, change the name to be in italic markdown.
    if (namedArmor && armorName?.toLowerCase() !== 'natural armor') {
      armorName = '_' + armorName + '_';
    }

    return {name:    armorName || '',
            type:    type,
            ac:      ac,
            minStr:  0,
            stealth: 'yes'};
  }

  const buildDescription = (armor: Armor | null, shield: boolean): string => {
    let name = armor && armor.name || '';
    if (name.toLowerCase() === 'shield') {
      name = '';
    } else if (name.toLowerCase().includes(', shield')) {
      name = name.replace(/, shield/gi, '').trim() || '';
    }
    if (name && shield) {
      return `${name}, Shield`;
    }
    return name || (shield && 'Shield') || '';
  }

  const handleArmorChange = (selectedValuesArray: string[]) => {
    // anytime they change armor, set the deviation back to 0
    setDeviation(0);
    const newArmorName = selectedValuesArray.length !== 0 && selectedValuesArray[0] || '';
    let newArmor = Armor.getArmor(newArmorName);
    let newCustom = !!newArmorName && !newArmor;
    if (!newArmor) {
      const type = armorType?.type || 'heavy';
      const baseAc = armorClass - Armor.buildDexBonus(type, dexterity) - (hasShield ? 2 : 0);
      newArmor = customArmor(newArmorName, type, baseAc);
    }
    if (isCustom != newCustom) {
      setIsCustom(newCustom);
    }
    const newAC = Armor.buildAC(newArmor, dexterity, hasShield);
    const newArmorDesc = buildDescription(armorType, hasShield);
    onSave(newAC, newArmorDesc, newArmor);
  };

  const handleChangeDeviation = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDev = Number(event.target.value);
    setDeviation(newDev);
    const newAC = Armor.buildAC(armorType, dexterity, hasShield) + newDev;
    onSave(newAC, armorDesc, armorType);
  }

  const handleChangeShield = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newHasShield = event.target.checked;
    setHasShield(newHasShield);
    const newAC = Armor.buildAC(armorType, dexterity, newHasShield) + deviation;
    const newDesc = buildDescription(armorType, newHasShield);
    onSave(newAC, newDesc, armorType);
  };

  const valueTextRef = useRef<HTMLSpanElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (valueTextRef.current && !valueTextRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  const types = Armor.getArmorNames();
  const optionClassMap: Record<string, string> = {};
  for (const armor of Armor.armors) {
    let className = '';
    if (armor.name === '') {
      className = 'armor-none';
    } else if (armor.minStr > strength) {
      className = 'armor-too-heavy';
    } else if (armor.type === 'light') {
      className = 'armor-light';
    } else if (armor.type === 'medium') {
      className = 'armor-medium';
    } else if (armor.type === 'heavy') {
      className = 'armor-heavy';
    }
    if (className) {
      optionClassMap[armor.name] = className;
    }
  }

  const handleTypeChange = (newType: 'light' | 'medium' | 'heavy' | 'special' | 'minAc') => {
    if (isCustom && armorType) {
      const newArmorType: Armor = {
        name:    armorType.name,
        type:    newType,
        ac:      armorType.ac,
        minStr:  armorType.minStr,
        stealth: armorType.stealth
      }
      let newAC = Armor.buildAC(newArmorType, dexterity, hasShield) + deviation;
      onSave(newAC, armorDesc, newArmorType);
    }
  }

  const trailer = (<>
        <div className='radio-group' style={{ fontSize: 'small', marginTop: '-47px', marginLeft: '230px', marginBottom: '10px', lineHeight: '0px'}}>
          <label style={{color: 'rgba(146, 38, 16, ' + (isCustom ? 1 : .5) + ')'}}>
            <input id="isLight"  disabled={!isCustom} checked={armorType?.type=='light'}  onChange={() => handleTypeChange('light')}  type="radio" name='armorType' />
            light
          </label>
          <label style={{color: 'rgba(146, 38, 16, ' + (isCustom ? 1 : .5) + ')'}}>
            <input id="isMedium" disabled={!isCustom} checked={armorType?.type=='medium'} onChange={() => handleTypeChange('medium')} type="radio" name='armorType' />
            med.
          </label>
          <label style={{color: 'rgba(146, 38, 16, ' + (isCustom ? 1 : .5) + ')'}}>
            <input id="isHeavy"  disabled={!isCustom} checked={armorType?.type=='heavy'}  onChange={() => handleTypeChange('heavy')}  type="radio" name='armorType' />
            heavy
          </label>
        </div>
      <div style={{display: 'flex', justifyContent: 'center', whiteSpace: 'nowrap'}}>
          <table style={{border: '1px solid black', borderRadius: '15px', padding: '3px 25px', background: 'rgba(0,0,0,.05)'}}>
            <tbody>
              <tr>
                <th>Base AC:</th>
                <td>{armorType?.ac || 10}</td>
              </tr>
              <tr>
                <th><label htmlFor="hasShield"><input id="hasShield" type="checkbox" checked={hasShield} onChange={handleChangeShield} style={{ marginLeft: '-15px' }} />Shield</label></th>
                <td>{hasShield ? '+2' : '--'}</td>
              </tr>
              <tr>
                <th>Dex Bonus:</th>
                <td style={{width: '110px'}}>+ {maxDexBonus}{(armorType && (maxDexBonus < dexBonus)) ? ` (${armorType?.type} armor)` : ''}</td>
              </tr>
              <tr>
                <th>Deviation:</th>
                <td>
                   + <input id="deviation" type="number" value={deviation} style={{ width: '40px', height: '16px', marginRight: '20px' }} onChange={handleChangeDeviation} />
                </td>
              </tr>
              <tr>
                <th>Final AC:</th>
                <td style={{padding: '10px'}}>
                  <span style={{ fontSize: 'xx-large', fontWeight: 700, fontStyle:'italic', background: '' }}>
                    {armorClass}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
      </div>
      {(armorType && armorType.minStr > strength) && (
        <div style={{ color: 'red', textAlign: 'center' }}>
          <br />
          STR is too low! (min {armorType?.minStr}), reduce speed by 10'
          <br />
          (must be applied manually)
        </div>
      )}
    </>);

  const tooltipFunction = (armorName: string): string => {
    const armor = Armor.getArmor(armorName);
    if (armor == null) return '';
    const minStr = armor.minStr ? `, min STR: ${armor.minStr}` : '';
    const stealth = armor.stealth === 'yes' ? ', stealthy' : '';
    return `AC: ${armor.ac}${minStr}${stealth}`;
  }
  return (
    <span className="multi-select-container edit-field">
      {isOpen && (
        <MultiSelectPopup
          values={armorType?.name || ''}
          options={types}
          optionClassMap={optionClassMap}
          singleSelection={true}
          allowCustom={true}
          onChange={handleArmorChange}
          onClickOutside={handleClickOutside}
          tooltipFunction={tooltipFunction}
          trailer={trailer}
        />
      )}
      <span onMouseDown={() => setIsOpen(true)} ref={valueTextRef}>
       <ReactMarkdown>{(armorDesc && !useMm2024) ? `${armorClass} (${armorDesc})` : `${armorClass}`}</ReactMarkdown>
      </span>
    </span>
  );
};

export default ArmorSelector;
